import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ObjectTable,
  type ObjectTableProps,
} from 'components/ObjectTable/ObjectTable';
import {
  SelectObjectsModal,
  type SelectObjectsModalProps,
} from 'components/SelectObjectsModal/SelectObjectsModal';
import { getText } from 'i18n/intl';
import type { TFindObjectsPayload } from 'slices/object.slice';
import { selectObjectTypesList } from 'slices/objectTypes.slice';
import { useAppSelector } from 'slices/store';
import type { IAPIResult } from 'types/state.interface';
import { TTEObject } from '@timeedit/types';
import { Button, Tooltip } from '@timeedit/ui-components';
import type { RelationFilter } from '../api/relationsApi';
import { useRelationList } from '../hooks/useRelationList';

const TABLE_ID = 'objects.relations';

type Props = {
  object: TTEObject;
  onChange: (patch: Partial<TTEObject>) => void;
};

// Strip keys ObjectTable supplies that are NOT user filter fields.
const toRelationFilter = (payload: TFindObjectsPayload): RelationFilter => {
  const {
    page: _p,
    limit: _l,
    ids: _i,
    excludeIds: _e,
    excludeExtIds: _ee,
    ...rest
  } = payload;
  return rest;
};

export const RelationList = ({ object, onChange }: Props) => {
  const objectTypes = useAppSelector(selectObjectTypesList);

  const {
    visibleItems,
    totalCount,
    isFetching,
    pendingAddIds,
    loadPage,
    reset,
    add,
    remove,
  } = useRelationList({ object, onChange });

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<TTEObject[]>([]);

  // Trim selection when items disappear (after Remove or filter change).
  useEffect(() => {
    const ids = new Set(visibleItems.map((o) => o.id));
    setSelectedRowKeys((prev) => prev.filter((k) => ids.has(Number(k))));
    setSelectedRows((prev) => prev.filter((o) => ids.has(o.id)));
  }, [visibleItems]);

  // Modal is an "add" picker. Existing relations show as preselected (visual only); removal
  // stays on the footer Remove button. The hook's add() dedupes against `object.relations` so
  // resolving preselected items into full TTEObjects isn't needed (and we deliberately skip
  // that prefetch via `prefetchSelected={false}` on the modal — see below).
  const handleModalClose = useCallback<
    NonNullable<SelectObjectsModalProps['onClose']>
  >(
    (selected) => {
      setIsAddModalOpen(false);
      if (selected && selected.length > 0) add(selected);
    },
    [add]
  );

  const handleRemoveSelected = useCallback(() => {
    if (selectedRows.length === 0) return;
    remove(selectedRows);
  }, [remove, selectedRows]);

  // ObjectTable owns pagination (page number, scroll trigger). The filter is read directly out
  // of the payload it hands us, so there's no captured stale state in the hook. We forward the
  // fetched items as `results` so AdminTable's useInfiniteScroll sees an accurate `count` /
  // `data` per fetch for its internal paging bookkeeping. (Rendering still goes through
  // `objects={visibleItems}`.)
  const fetchFnShim: ObjectTableProps['fetchFn'] = useCallback(
    async (payload: TFindObjectsPayload): Promise<IAPIResult<TTEObject>> => {
      const filter = toRelationFilter(payload);
      const page = payload.page ?? 1;
      // Use loadPage's live totalCount return — the captured `totalCount` from this closure
      // hasn't seen the just-fetched grossTotal yet (React state hasn't flushed).
      const {
        hasMore,
        totalCount: liveTotal,
        items,
      } = await loadPage({ filter, page });
      // `IAPIResult` wraps results as `TDocument<TTEObject>` (string id + timestamps), but
      // `findMoreObjects` already returns plain TTEObject at runtime — same shape this codebase
      // uses everywhere else for object responses. Cast at the boundary to match.
      return {
        results: items as unknown as IAPIResult<TTEObject>['results'],
        page,
        limit: payload.limit ?? 200,
        totalPages: hasMore ? page + 1 : page,
        totalResults: liveTotal,
      };
    },
    [loadPage]
  );

  // ObjectTable calls this on filter / sort change. Clearing serverItems makes
  // AdminTable's useInfiniteScroll see items.length === 0 and re-trigger getMore,
  // which routes through fetchFnShim with the *new* filter payload.
  const resetFn: ObjectTableProps['resetFn'] = useCallback(() => {
    reset();
  }, [reset]);

  const isAddDisabled = objectTypes.length === 0;

  const footerContent: ObjectTableProps['footerContent'] = useMemo(
    () => [
      <Tooltip
        key="add-tooltip"
        title={
          isAddDisabled
            ? getText(
                'object_manager.object_table.drawer.associations.actions.add.tooltip.disabled'
              )
            : undefined
        }
      >
        <Button
          key="add"
          type="primary"
          onClick={() => setIsAddModalOpen(true)}
          disabled={isAddDisabled}
        >
          {getText(
            'object_manager.object_table.drawer.associations.actions.add'
          )}
        </Button>
      </Tooltip>,
      <Button
        key="remove"
        type="default"
        disabled={selectedRows.length === 0}
        onClick={handleRemoveSelected}
      >
        {getText(
          'object_manager.object_table.drawer.associations.actions.remove'
        )}
      </Button>,
    ],
    [isAddDisabled, selectedRows.length, handleRemoveSelected]
  );

  const rowSelection: ObjectTableProps['rowSelection'] = useMemo(
    () => ({
      selectedRowKeys,
      onChange: (keys, rows) => {
        setSelectedRowKeys(keys);
        setSelectedRows(rows as TTEObject[]);
      },
      preserveSelectedRowKeys: true,
      hideSelectAll: true,
      columnWidth: 32,
    }),
    [selectedRowKeys]
  );

  // Pending-added rows reuse the existing FieldChangedMark indicator (`changedIds`).
  const changedIds = useMemo(
    () => Array.from(pendingAddIds).map(String),
    [pendingAddIds]
  );

  // Full preselect set for the Add modal — flattened from object.relations so it covers
  // every relation regardless of how far the user has scrolled the list.
  const preselectedRelationIds = useMemo(
    () => object.relations.flatMap((r) => r.objectIds),
    [object.relations]
  );

  return (
    <>
      <ObjectTable
        tableId={TABLE_ID}
        objects={visibleItems}
        availableObjectTypes={objectTypes}
        fetchFn={fetchFnShim}
        resetFn={resetFn}
        totalItemCount={totalCount}
        // Both numbers in "Showing X of Y" come from us — there's no separate server-filter
        // narrowing within a larger total to display.
        filteredTotalOverride={totalCount}
        scroll={{ y: '80vh' }}
        rowSelection={rowSelection}
        changedIds={changedIds}
        loading={isFetching}
        footerContent={footerContent}
      />
      <SelectObjectsModal
        open={isAddModalOpen}
        onClose={handleModalClose}
        availableObjectTypes={objectTypes}
        // Use the full relation set from Redux (working copy, including pending adds and minus
        // pending removes), not `visibleItems` — the latter is only what's been infinite-scroll
        // loaded. `prefetchSelected={false}` skips fetching full TTEObjects for them up-front;
        // the hook's add() handles dedupe against existing items so resolution isn't needed.
        selectedObjectIds={preselectedRelationIds}
        prefetchSelected={false}
      />
    </>
  );
};
